import React, { useCallback, useEffect, useRef, useState } from 'react';
import './chartDrawings.css';

// ---------------------------------------------------------------------------
// Drawing tools for the candlestick chart (left toolbar + SVG overlay).
//
// Tools: cursor, trend line, ray, horizontal line, vertical line, rectangle,
// Fibonacci retracement, text note, measure. Click a drawing to select it;
// Delete/Backspace or the trash button removes it; "clear" removes all.
// Drawings are stored per market in this browser (localStorage) as
// {time, price} points, so they stay on the right candles when you zoom,
// pan, or switch interval.
//
// lightweight-charts has no built-in drawings; coordinates are converted
// with timeScale().logicalToCoordinate / coordinateToLogical (via the candle
// times) and series.priceToCoordinate / coordinateToPrice.
// ---------------------------------------------------------------------------

const FIB = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1];
const TOOLS = [
  ['cursor', 'Cursor', 'M5 3l12 9-5.5 1.2L15 20l-2.4 1.1-3.2-6.6L5 18z'],
  ['trend', 'Trend line', 'M4 20L20 4M4 20a1.6 1.6 0 1 0 .01 0M20 4a1.6 1.6 0 1 0 .01 0'],
  ['ray', 'Ray', 'M4 18L22 6M4 18a1.6 1.6 0 1 0 .01 0'],
  ['hline', 'Horizontal line', 'M2 12h20M12 12a1.6 1.6 0 1 0 .01 0'],
  ['vline', 'Vertical line', 'M12 2v20M12 12a1.6 1.6 0 1 0 .01 0'],
  ['rect', 'Rectangle', 'M4 6h16v12H4z'],
  ['fib', 'Fib retracement', 'M3 5h18M3 9h18M3 12h18M3 15h18M3 19h18'],
  ['text', 'Text note', 'M5 5h14M12 5v14M9 19h6'],
  ['measure', 'Measure', 'M4 20L20 4M4 14v6h6M14 4h6v6']
];
const TWO_POINT = new Set(['trend', 'ray', 'rect', 'fib', 'measure']);

const load = (key) => {
  try {
    const v = JSON.parse(window.localStorage.getItem(key) || '[]');
    return Array.isArray(v) ? v : [];
  } catch (e) {
    return [];
  }
};
const save = (key, v) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(v));
  } catch (e) {
    /* storage unavailable - drawings just won't persist */
  }
};

export default function ChartDrawings({ chart, series, candlesRef, intervalSec, storageKey, fmtPrice }) {
  const wrapRef = useRef(null);
  const [tool, setTool] = useState('cursor');
  const [items, setItems] = useState(() => load(storageKey));
  const [draft, setDraft] = useState(null); // {type, a, b}
  const [selected, setSelected] = useState(null);
  const [textAt, setTextAt] = useState(null); // {x, y, point}
  const [, setTick] = useState(0);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    setItems(load(storageKey));
    setSelected(null);
    setDraft(null);
  }, [storageKey]);
  useEffect(() => save(storageKey, items), [storageKey, items]);

  // Redraw whenever the chart moves or resizes.
  useEffect(() => {
    if (!chart) return undefined;
    let raf = 0;
    const bump = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setTick((t) => t + 1));
    };
    chart.timeScale().subscribeVisibleLogicalRangeChange(bump);
    chart.subscribeCrosshairMove(bump);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(bump) : null;
    if (ro && wrapRef.current) ro.observe(wrapRef.current);
    // Price-scale autoscale has no event; a slow heartbeat keeps lines glued.
    const iv = setInterval(bump, 500);
    return () => {
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(bump);
      chart.unsubscribeCrosshairMove(bump);
      if (ro) ro.disconnect();
      clearInterval(iv);
      cancelAnimationFrame(raf);
    };
  }, [chart]);

  // ---- time <-> logical (handles times between / beyond candles) ----------
  const times = () => (candlesRef.current || []).map((c) => c.time);
  const timeToLogical = useCallback(
    (t) => {
      const ts = times();
      if (!ts.length) return null;
      if (t <= ts[0]) return (t - ts[0]) / intervalSec;
      const last = ts.length - 1;
      if (t >= ts[last]) return last + (t - ts[last]) / intervalSec;
      let lo = 0;
      let hi = last;
      while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (ts[mid] <= t) lo = mid;
        else hi = mid;
      }
      return lo + (t - ts[lo]) / Math.max(1, ts[hi] - ts[lo]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intervalSec]
  );
  const logicalToTime = (l) => {
    const ts = times();
    if (!ts.length) return null;
    const last = ts.length - 1;
    if (l <= 0) return ts[0] + l * intervalSec;
    if (l >= last) return ts[last] + (l - last) * intervalSec;
    const i = Math.floor(l);
    return ts[i] + (l - i) * (ts[i + 1] - ts[i]);
  };
  const toXY = (p) => {
    if (!chart || !series || !p) return null;
    const l = timeToLogical(p.time);
    const x = l === null ? null : chart.timeScale().logicalToCoordinate(l);
    const y = series.priceToCoordinate(p.price);
    return x === null || y === null || x === undefined || y === undefined ? null : { x, y };
  };
  const fromEvent = (e) => {
    const r = wrapRef.current.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    const l = chart.timeScale().coordinateToLogical(x);
    const price = series.coordinateToPrice(y);
    const time = l === null ? null : logicalToTime(l);
    return time === null || price === null ? null : { time, price, x, y };
  };

  const onDown = (e) => {
    if (tool === 'cursor' || !chart || !series) return;
    e.preventDefault();
    const p = fromEvent(e);
    if (!p) return;
    const point = { time: p.time, price: p.price };
    if (tool === 'text') {
      setTextAt({ x: p.x, y: p.y, point });
      return;
    }
    if (tool === 'hline' || tool === 'vline') {
      setItems((v) => [...v, { id: Date.now(), type: tool, a: point }]);
      setTool('cursor');
      return;
    }
    if (TWO_POINT.has(tool)) {
      if (!draft) setDraft({ type: tool, a: point, b: point });
      else {
        const done = { ...draft, b: point };
        setDraft(null);
        if (tool === 'measure') setDraft({ ...done, frozen: true });
        else setItems((v) => [...v, { id: Date.now(), ...done }]);
        if (tool !== 'measure') setTool('cursor');
      }
    }
  };
  const onMove = (e) => {
    if (!draft || draft.frozen) return;
    const p = fromEvent(e);
    if (p) setDraft((d) => (d ? { ...d, b: { time: p.time, price: p.price } } : d));
  };

  useEffect(() => {
    const k = (e) => {
      if (e.key === 'Escape') {
        setDraft(null);
        setTool('cursor');
        setTextAt(null);
        setSelected(null);
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selected && !textAt) {
        const tag = (e.target?.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea') return;
        setItems((v) => v.filter((i) => i.id !== selected));
        setSelected(null);
      }
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [selected, textAt]);

  const box = wrapRef.current?.getBoundingClientRect();
  const W = box?.width || 0;
  const H = box?.height || 0;

  const shape = (it, isDraft) => {
    const a = toXY(it.a);
    const sel = !isDraft && selected === it.id;
    const common = {
      className: `cd-shape${sel ? ' cd-sel' : ''}`,
      onMouseDown: isDraft
        ? undefined
        : (e) => {
            if (tool !== 'cursor') return;
            e.stopPropagation();
            setSelected(it.id);
          }
    };
    if (it.type === 'hline') {
      if (!a) return null;
      return (
        <g key={it.id} {...common}>
          <line x1={0} x2={W} y1={a.y} y2={a.y} />
          <line className="cd-hit" x1={0} x2={W} y1={a.y} y2={a.y} />
          <text className="cd-label" x={W - 6} y={a.y - 4} textAnchor="end">
            {fmtPrice(it.a.price)}
          </text>
        </g>
      );
    }
    if (it.type === 'vline') {
      if (!a) return null;
      return (
        <g key={it.id} {...common}>
          <line x1={a.x} x2={a.x} y1={0} y2={H} />
          <line className="cd-hit" x1={a.x} x2={a.x} y1={0} y2={H} />
        </g>
      );
    }
    if (it.type === 'text') {
      if (!a) return null;
      return (
        <g key={it.id} {...common}>
          <text className="cd-note" x={a.x} y={a.y}>
            {it.text}
          </text>
        </g>
      );
    }
    const b = toXY(it.b);
    if (!a || !b) return null;
    if (it.type === 'trend') {
      return (
        <g key={it.id} {...common}>
          <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} />
          <line className="cd-hit" x1={a.x} y1={a.y} x2={b.x} y2={b.y} />
          <circle className="cd-end" cx={a.x} cy={a.y} r={3} />
          <circle className="cd-end" cx={b.x} cy={b.y} r={3} />
        </g>
      );
    }
    if (it.type === 'ray') {
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const k = dx === 0 ? 0 : (W * 4) / Math.abs(dx);
      const ex = a.x + dx * (dx === 0 ? 0 : k);
      const ey = a.y + dy * (dx === 0 ? 0 : k);
      return (
        <g key={it.id} {...common}>
          <line x1={a.x} y1={a.y} x2={dx === 0 ? b.x : ex} y2={dx === 0 ? (dy > 0 ? H : 0) : ey} />
          <line className="cd-hit" x1={a.x} y1={a.y} x2={b.x} y2={b.y} />
          <circle className="cd-end" cx={a.x} cy={a.y} r={3} />
        </g>
      );
    }
    if (it.type === 'rect') {
      return (
        <g key={it.id} {...common}>
          <rect className="cd-fill" x={Math.min(a.x, b.x)} y={Math.min(a.y, b.y)} width={Math.abs(b.x - a.x)} height={Math.abs(b.y - a.y)} />
        </g>
      );
    }
    if (it.type === 'fib') {
      const x1 = Math.min(a.x, b.x);
      const x2 = Math.max(a.x, b.x, x1 + 60);
      return (
        <g key={it.id} {...common}>
          {FIB.map((f) => {
            const price = it.b.price + (it.a.price - it.b.price) * f;
            const y = series.priceToCoordinate(price);
            if (y === null || y === undefined) return null;
            return (
              <g key={f}>
                <line className="cd-fib" x1={x1} x2={x2} y1={y} y2={y} />
                <line className="cd-hit" x1={x1} x2={x2} y1={y} y2={y} />
                <text className="cd-label" x={x1 + 4} y={y - 3}>
                  {f} ({fmtPrice(price)})
                </text>
              </g>
            );
          })}
        </g>
      );
    }
    if (it.type === 'measure') {
      const dp = it.b.price - it.a.price;
      const pct = it.a.price ? (dp / it.a.price) * 100 : 0;
      const bars = Math.round((timeToLogical(it.b.time) ?? 0) - (timeToLogical(it.a.time) ?? 0));
      const up = dp >= 0;
      return (
        <g key="measure" className={`cd-measure ${up ? 'cd-up' : 'cd-down'}`}>
          <rect x={Math.min(a.x, b.x)} y={Math.min(a.y, b.y)} width={Math.abs(b.x - a.x)} height={Math.abs(b.y - a.y)} />
          <text className="cd-mlabel" x={(a.x + b.x) / 2} y={Math.min(a.y, b.y) - 6} textAnchor="middle">
            {`${up ? '+' : ''}${fmtPrice(dp)} (${up ? '+' : ''}${pct.toFixed(2)}%) · ${bars} bars`}
          </text>
        </g>
      );
    }
    return null;
  };

  return (
    <>
      <div className="cd-toolbar" role="toolbar" aria-label="Drawing tools">
        {TOOLS.map(([k, label, d]) => (
          <button
            key={k}
            type="button"
            title={label}
            aria-label={label}
            className={tool === k ? 'cd-tool cd-active' : 'cd-tool'}
            onClick={() => {
              setTool(k);
              setDraft(null);
              setTextAt(null);
            }}
          >
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path d={d} />
            </svg>
          </button>
        ))}
        <span className="cd-sep" />
        <button
          type="button"
          title={hidden ? 'Show drawings' : 'Hide drawings'}
          className={hidden ? 'cd-tool cd-active' : 'cd-tool'}
          onClick={() => setHidden((h) => !h)}
        >
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path d={hidden ? 'M3 3l18 18M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z' : 'M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12zM12 9a3 3 0 1 0 .01 0'} />
          </svg>
        </button>
        <button
          type="button"
          title={selected ? 'Delete selected drawing' : 'Delete all drawings'}
          className="cd-tool"
          onClick={() => {
            if (selected) {
              setItems((v) => v.filter((i) => i.id !== selected));
              setSelected(null);
            } else if (items.length) setItems([]);
            setDraft(null);
          }}
        >
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6" />
          </svg>
        </button>
      </div>
      <div
        ref={wrapRef}
        className={`cd-overlay${tool !== 'cursor' ? ' cd-drawing' : ''}`}
        onMouseDown={onDown}
        onMouseMove={onMove}
        onClick={() => tool === 'cursor' && setSelected(null)}
      >
        {!hidden && W > 0 && (
          <svg width={W} height={H}>
            {items.map((it) => shape(it, false))}
            {draft && shape({ ...draft, id: 'draft' }, true)}
          </svg>
        )}
        {textAt && (
          <input
            className="cd-text-input"
            style={{ left: textAt.x, top: textAt.y - 12 }}
            autoFocus
            placeholder="Note… (Enter)"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                const text = e.currentTarget.value.trim().slice(0, 120);
                setItems((v) => [...v, { id: Date.now(), type: 'text', a: textAt.point, text }]);
                setTextAt(null);
                setTool('cursor');
              }
            }}
            onBlur={() => setTextAt(null)}
          />
        )}
      </div>
    </>
  );
}
